/**
 * How a result reads (tasks 1177, 1180) — one set of rules for the summary
 * strip, the result rows, the trend dots and the Needs review filter.
 *
 * Phase 1 honesty rule (report section 7): transport completion is never shown
 * as passing. A result is **completed** until a person marks it; **passed**
 * means a human said so. The human verdict always wins the displayed outcome.
 */
import type {
  EndpointTestRunGroup,
  EndpointTestRunGroupSummary,
  EndpointTestRunResult,
  EndpointTestRunScore,
} from '@/types/endpointTests';

/** Transport statuses that mean the invocation has not finished. */
const PENDING_STATUSES = new Set(['pending', 'queued', 'running', 'in_progress', 'processing', 'waiting']);

export type ResultTone = 'pass' | 'fail' | 'errored' | 'uncertain' | 'completed' | 'pending';

export interface ResultDisplay {
  tone: ResultTone;
  /** The row's label: "human pass", "completed", "errored", "running…". */
  label: string;
  /** The verdict came from a person. */
  human: boolean;
  /** A secondary line beside the label (task 1180: "grader fail · <criterion>"). */
  note?: string;
}

export const isPendingStatus = (status: string | null | undefined) =>
  !!status && PENDING_STATUSES.has(status);

export const isSettled = (result: EndpointTestRunResult) => !isPendingStatus(result.status);

/**
 * How long a landed result without a roll-up is still treated as being graded.
 * The service writes the result row first and evaluates its criteria after
 * (one grader call for graded criteria), so a fresh row with no
 * `evaluatedOutcome` may still be mid-evaluation; a test with no criteria
 * simply never gets one, and stops being polled once this passes.
 */
export const EVALUATION_GRACE_MS = 30_000;

/** A result is final once it has executed and its criteria are evaluated (or the grace has passed). */
export const isResultFinal = (result: EndpointTestRunResult, now = Date.now()) =>
  isSettled(result) &&
  (result.evaluatedOutcome != null ||
    result.outcome === 'errored' ||
    now - Date.parse(result.createdAt) > EVALUATION_GRACE_MS);

/** What a scores read should be keyed by, so it refreshes when the result changes. */
export const resultRevision = (result: EndpointTestRunResult) =>
  [result.evaluatedOutcome ?? '', result.humanScore?.id ?? '', result.scores?.length ?? 0].join(':');

export const isErroredStatus = (status: string | null | undefined) =>
  !!status && !isPendingStatus(status) && status !== 'completed';

export const humanScoreOf = (result: EndpointTestRunResult): EndpointTestRunScore | undefined =>
  result.humanScore ?? result.scores?.find((score) => score.kind === 'human');

/** A score's criterion under either key the two reads use. */
export const scoreCriterion = (score: EndpointTestRunScore) =>
  score.criterion ?? score.endpointTestCriterion ?? null;

/** A result's duration in ms. */
export const resultDuration = (result: EndpointTestRunResult): number | null =>
  result.duration ?? result.collaboration?.duration ?? null;

/** Criterion scores (checks and graded criteria) on a result, without the human verdict. */
export const criterionScoresOf = (result: EndpointTestRunResult): EndpointTestRunScore[] =>
  (result.scores ?? []).filter((score) => score.kind !== 'human');

/** The criterion a row names: the first failing required one, else the first uncertain one. */
export function decidingCriterion(result: EndpointTestRunResult): EndpointTestRunScore | undefined {
  const scores = criterionScoresOf(result);
  const gating = (score: EndpointTestRunScore) => scoreCriterion(score)?.required !== false;
  return (
    scores.find((score) => score.outcome === 'fail' && gating(score)) ??
    scores.find((score) => score.outcome === 'uncertain' && gating(score))
  );
}

const criterionName = (score: EndpointTestRunScore | undefined) =>
  (score && scoreCriterion(score)?.name) ?? 'criterion';

/**
 * The outcome a result displays (tasks 1177, 1180), with the human verdict
 * taking precedence. Phase 2: without a human verdict the roll-up
 * (`evaluatedOutcome`) decides, and the row names the first failing
 * criterion. A human verdict that disagrees with the roll-up shows both
 * ("human pass" · "grader fail · <criterion>").
 */
export function resultDisplay(result: EndpointTestRunResult): ResultDisplay {
  if (isPendingStatus(result.status)) return { tone: 'pending', label: 'running…', human: false };
  const human = humanScoreOf(result);
  const rollUp = result.evaluatedOutcome ?? null;
  if (human && (human.outcome === 'pass' || human.outcome === 'fail')) {
    const display: ResultDisplay = { tone: human.outcome, label: `human ${human.outcome}`, human: true };
    if (rollUp && rollUp !== human.outcome) {
      const deciding = decidingCriterion(result);
      display.note = deciding ? `grader ${rollUp} · ${criterionName(deciding)}` : `grader ${rollUp}`;
    }
    return display;
  }
  if (result.outcome === 'errored' || isErroredStatus(result.status)) {
    return { tone: 'errored', label: 'errored', human: false };
  }
  if (rollUp === 'fail' || rollUp === 'uncertain') {
    const deciding = decidingCriterion(result);
    const label = deciding
      ? rollUp === 'fail'
        ? `✗ ${criterionName(deciding)}`
        : `? ${criterionName(deciding)}`
      : rollUp;
    return { tone: rollUp, label, human: false };
  }
  if (rollUp === 'pass') {
    const count = criterionScoresOf(result).length;
    const label = count > 0 ? `${count} ${count === 1 ? 'criterion' : 'criteria'} passed` : 'passed';
    return { tone: 'pass', label, human: false };
  }
  return { tone: 'completed', label: 'completed', human: false };
}

/** Failures first (report section 6): fail, errored, uncertain, running, completed, pass. */
const TONE_ORDER: Record<ResultTone, number> = {
  fail: 0,
  errored: 1,
  uncertain: 2,
  pending: 3,
  completed: 4,
  pass: 5,
};

export function sortResults(results: EndpointTestRunResult[]): EndpointTestRunResult[] {
  return results
    .map((result, index) => ({ result, index, tone: resultDisplay(result).tone }))
    .sort((a, b) => TONE_ORDER[a.tone] - TONE_ORDER[b.tone] || a.index - b.index)
    .map((entry) => entry.result);
}

/** What a summary needs from its test (task 1229): what was asked for, whether it finished, what was refused. */
type GroupCounts = Pick<EndpointTestRunGroup, 'testIds' | 'completedAt' | 'refusals'>;

/** The test's total: its requested scenarios (task 1228), else whatever landed (rows made before the column). */
const totalOf = (group: Partial<GroupCounts> | undefined, landed: number) =>
  Math.max(group?.testIds?.length ?? 0, landed);

/**
 * The strip's counts, computed from the results themselves (task 1229). The
 * total is `testIds.length`; `notRun` is `refusals.length`; `pending` is what
 * is neither landed nor refused, and only while `completedAt` is null.
 */
export function summarizeResults(
  results: EndpointTestRunResult[],
  group?: Partial<GroupCounts>,
): EndpointTestRunGroupSummary {
  const notRun = group?.refusals?.length ?? 0;
  const running = group?.completedAt === null;
  const summary: Required<Omit<EndpointTestRunGroupSummary, 'duration'>> & { duration: number | null } = {
    passed: 0,
    failed: 0,
    completed: 0,
    errored: 0,
    uncertain: 0,
    reviewed: 0,
    pending: 0,
    notRun,
    tests: totalOf(group, results.length + notRun),
    duration: null,
  };
  summary.pending = running ? Math.max(0, summary.tests - results.length - notRun) : 0;
  for (const result of results) {
    const { tone } = resultDisplay(result);
    if (tone === 'pass') summary.passed += 1;
    else if (tone === 'fail') summary.failed += 1;
    else if (tone === 'errored') summary.errored += 1;
    else if (tone === 'uncertain') summary.uncertain += 1;
    else if (tone === 'pending') summary.pending += 1;
    else summary.completed += 1;
    if (humanScoreOf(result)) summary.reviewed += 1;
    const duration = resultDuration(result);
    if (typeof duration === 'number') summary.duration = (summary.duration ?? 0) + duration;
  }
  return summary;
}

/**
 * A list row's counts (task 1229): the service's summary of what landed, with
 * the total, the refused and — while the test runs — the outstanding counts
 * from the test itself.
 */
export function groupSummary(group: EndpointTestRunGroup & Partial<GroupCounts>): EndpointTestRunGroupSummary {
  const base: EndpointTestRunGroupSummary = group.summary ?? {
    tests: 0, passed: 0, failed: 0, uncertain: 0, completed: 0, errored: 0, reviewed: 0, duration: null,
  };
  const landed = base.tests ?? 0;
  const notRun = group.refusals?.length ?? 0;
  const tests = totalOf(group, landed + notRun);
  const pending = group.completedAt === null ? Math.max(0, tests - landed - notRun) : 0;
  return { ...base, tests, notRun, pending };
}

/** Results Re-run failed would pick up: failed verdicts, transport errors and refused scenarios (task 1229). */
export const failedCount = (summary: EndpointTestRunGroupSummary) =>
  summary.failed + summary.errored + (summary.notRun ?? 0);

/** Total results the strip describes. */
export const testCount = (summary: EndpointTestRunGroupSummary) =>
  summary.tests ??
  summary.passed +
    summary.failed +
    summary.completed +
    summary.errored +
    (summary.uncertain ?? 0) +
    (summary.pending ?? 0) +
    (summary.notRun ?? 0);

/** Needs review: any result without a human verdict, or any uncertain roll-up (task 1180). */
export const needsReview = (summary: EndpointTestRunGroupSummary) =>
  // A refused scenario has no result to review (task 1229).
  summary.reviewed < testCount(summary) - (summary.notRun ?? 0) || (summary.uncertain ?? 0) > 0;

/** "41s", "3.1s", "820ms". */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 10) return `${seconds.toFixed(1)}s`;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${Math.round(seconds % 60)}s`;
}

/** Dot colour per tone. */
export const TONE_DOT_CLASS: Record<ResultTone, string> = {
  pass: 'bg-green-500',
  fail: 'bg-red-500',
  errored: 'bg-red-500',
  uncertain: 'bg-amber-500',
  completed: 'bg-gray-400',
  pending: 'bg-gray-400 animate-pulse',
};

/** Trend dot colour from a trend point's outcome string (task 1172's shape). */
export function trendDotClass(outcome: string | null): string {
  if (outcome === 'pass') return 'bg-green-500';
  if (outcome === 'fail' || outcome === 'errored' || outcome === 'failed' || outcome === 'error') return 'bg-red-500';
  if (outcome === 'uncertain') return 'bg-amber-500';
  return 'bg-gray-400';
}

// ---------------------------------------------------------------------------
// A test's identity (task 1232, on task 1231's `number` / `origin` columns)
// ---------------------------------------------------------------------------

const scenarioCount = (n: number) => `${n} ${n === 1 ? 'scenario' : 'scenarios'}`;

/** The bare URL's path: what a test with a version stamp and no stage ran against (task 1237). */
export const BARE_URL_PATH = '/';

/**
 * Where a test ran (task 1237), from its stamp — never from `environmentId` alone:
 *
 * - a stage and a version → "staging · v1.3";
 * - a version and no stage → "/ · v1.2" (the bare URL on a published service,
 *   which the service stamps since task 1235);
 * - no version → "working state", which is then only ever true: a service
 *   that has never been published. A stage with no version (a row made before
 *   the stamp) prints its path alone.
 */
export function targetLabel(
  test: { environment?: { path: string } | null; resourceVersion?: { major: number; minor: number } | null },
): string {
  const version = test.resourceVersion;
  const path = test.environment?.path ?? (version ? BARE_URL_PATH : null);
  if (!path) return 'working state';
  return version ? `${path} · v${version.major}.${version.minor}` : path;
}

/** "v1.3" from a collaboration's `endpointVersion` ("1.3", tolerating "v1.3"); null when the live path served it. */
export function servedVersionLabel(endpointVersion: string | null | undefined): string | null {
  if (!endpointVersion) return null;
  return `v${endpointVersion.replace(/^v/i, '')}`;
}

/** "#12", or '' while the service predates the column. */
export const testNumberLabel = (test: Pick<EndpointTestRunGroup, 'number'>) =>
  typeof test.number === 'number' ? `#${test.number}` : '';

/**
 * Where a test came from, in words (task 1232). `scenarioNames` maps scenario
 * id → name (the panel's scenarios), for a single-scenario test;
 * `numbers` maps test id → `number`, for a re-run's parent. Null when the
 * service sends no origin.
 */
export function originPhrase(
  test: Pick<EndpointTestRunGroup, 'origin' | 'testIds' | 'summary' | 'endpointTestSuite' | 'parentEndpointTestRunGroupId'>,
  scenarioNames: ReadonlyMap<string, string>,
  numbers: ReadonlyMap<string, number | undefined>,
): string | null {
  const count = test.testIds?.length || test.summary?.tests || 0;
  const parentNumber = test.parentEndpointTestRunGroupId ? numbers.get(test.parentEndpointTestRunGroupId) : undefined;
  const parent = typeof parentNumber === 'number' ? `#${parentNumber}` : 'an earlier test';
  switch (test.origin) {
    case 'suite':
      return `Suite ${test.endpointTestSuite?.name ?? 'Suite (deleted)'} · ${scenarioCount(count)}`;
    case 'assistant':
      return `From the Assistant · ${scenarioCount(count)}`;
    case 'selection':
      return `Selection · ${scenarioCount(count)}`;
    case 'single': {
      const id = test.testIds?.[0];
      return `1 scenario: ${(id && scenarioNames.get(id)) || 'Deleted scenario'}`;
    }
    case 'rerun':
      return `Re-run of ${parent} · ${scenarioCount(count)}`;
    case 'rerun_failed':
      return `Re-run of ${parent}'s failures · ${scenarioCount(count)}`;
    default:
      return null;
  }
}

/** "Today", "Yesterday", else the locale date — the day header a test falls under. */
export function dayHeader(iso: string, now = new Date()): string {
  const day = new Date(iso);
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(day)) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return day.toLocaleDateString();
}

/** The origin filter's match (task 1232): `suite:<id>`, `reruns` (both re-run kinds), or an origin. */
export function matchesOrigin(test: Pick<EndpointTestRunGroup, 'origin' | 'endpointTestSuiteId'>, filter: string): boolean {
  if (!filter) return true;
  if (filter === 'reruns') return test.origin === 'rerun' || test.origin === 'rerun_failed';
  if (filter.startsWith('suite:')) return test.origin === 'suite' && test.endpointTestSuiteId === filter.slice(6);
  return test.origin === filter;
}

// ---------------------------------------------------------------------------
// The in-progress card's estimate and stale rule (task 1233)
// ---------------------------------------------------------------------------

/** How long a settled test took, start to finish (`completedAt` − `createdAt`), or null. */
export function testWallDuration(test: Pick<EndpointTestRunGroup, 'createdAt' | 'completedAt'>): number | null {
  if (!test.completedAt) return null;
  const ms = Date.parse(test.completedAt) - Date.parse(test.createdAt);
  return Number.isFinite(ms) && ms > 0 ? ms : null;
}

/**
 * The expected total duration of a running test (task 1233), from the
 * settled tests the section already holds (newest first):
 *
 * 1. the most recent settled test on the same target with the same scenario
 *    count — its duration;
 * 2. else the most recent settled test of any shape, scaled by
 *    `running.testIds.length / that.testIds.length`;
 * 3. else null (elapsed only).
 *
 * A settled test with no landed results (every scenario refused) is skipped.
 */
export function estimateDuration(
  running: Pick<EndpointTestRunGroup, 'environmentId' | 'testIds'>,
  settled: Array<Pick<EndpointTestRunGroup, 'environmentId' | 'testIds' | 'createdAt' | 'completedAt' | 'summary'>>,
): number | null {
  const count = running.testIds?.length ?? 0;
  if (count === 0) return null;
  // Only a test where scenarios actually ran says how long they take: one
  // whose every scenario was refused settles in milliseconds (found in the
  // task 1233 live check, where it made a fresh test "stale" at once).
  const timed = settled.filter(
    (test) => (test.testIds?.length ?? 0) > 0 && (test.summary?.tests ?? 0) > 0 && testWallDuration(test) !== null,
  );
  const same = timed.find((test) => test.environmentId === running.environmentId && test.testIds.length === count);
  if (same) return testWallDuration(same);
  const any = timed[0];
  if (!any) return null;
  return Math.round((testWallDuration(any) as number) * (count / any.testIds.length));
}

/** With no estimate, a test is "running longer than usual" after this long. */
export const STALE_WITHOUT_ESTIMATE_MS = 5 * 60 * 1000;

/**
 * Whether a running test is taking too long (task 1233): past twice the
 * estimate, or past five minutes when there is no estimate. `link` is the
 * first result's collaboration run, when there is one, for the User to look at.
 */
export function staleState({
  elapsedMs,
  estimateMs,
  endpointId,
  firstCollaborationId,
}: {
  elapsedMs: number;
  estimateMs: number | null;
  endpointId: string;
  firstCollaborationId?: string | null;
}): { isStale: boolean; link: string | null } {
  const isStale = estimateMs !== null ? elapsedMs > 2 * estimateMs : elapsedMs > STALE_WITHOUT_ESTIMATE_MS;
  return {
    isStale,
    link: isStale && firstCollaborationId ? `/services/${endpointId}/runs/${firstCollaborationId}` : null,
  };
}

/** "1:10", "12:04" — a clock duration for elapsed and estimates. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
